import { Button, Flex, Typography } from "antd"
import dayjs from "dayjs"
import React, { useMemo } from "react"
import { useTranslation } from "react-i18next"

import { readAllDayDate } from "../../../../../utils/date/get-date-safe.ts"
import { type Event } from "../../../types/types.ts"
import {
  calendarEventCardRecordParts,
  calendarEventCardText
} from "../../helpers/calendar-event-card-text.ts"
import { getStoredEventColor } from "../../helpers/event-custom-colors.ts"
import { CalendarTimeAndType } from "../CalendarTimeAndType.tsx"

const { Text, Title } = Typography

interface CalendarMyScheduleProps {
  events: Event[]
  timezone: string
  userId?: string
  // The Calendar's own selected/navigated date (Calendar.tsx's
  // `selectedDate`/`availabilityDate`) — not necessarily today. This card
  // used to always read the real current date here regardless of what the
  // user was actually looking at, which is why it read empty even with
  // visible events on the selected day.
  currentDate: Date
  onSelectEvent: (id: string) => void
  onViewAll: () => void
}

const eventStart = (event: Event, timezone: string) =>
  event?.all_day
    ? readAllDayDate(event.start_at)
    : dayjs(event?.start_at).tz(timezone)

// The list endpoint nests created_by/participants/lead/project/category/
// contact under extended_props rather than on the event itself. Reading
// them at the top level silently matched nothing — this card always fell
// back to "created" and "shows nothing" — matching only on the rare event
// whose props happened to be duplicated at both levels.
const flattenEvent = (event: Event): Event => ({
  ...event,
  ...event?.extended_props
})

const isMine = (event: Event, userId?: string) => {
  if (!userId) return true
  const flat = flattenEvent(event)
  if (flat?.created_by?.id === userId) return true
  return !!flat?.participants?.some((p) => p?.id === userId)
}

export function CalendarMySchedule({
  events,
  timezone,
  userId,
  currentDate,
  onSelectEvent,
  onViewAll
}: CalendarMyScheduleProps) {
  const { t } = useTranslation()

  const selectedDay = dayjs(currentDate).tz(timezone)
  const now = dayjs().tz(timezone)
  const isViewingToday = selectedDay.isSame(now, "day")

  const scheduleEvents = useMemo(() => {
    return (events || [])
      .filter((event) => isMine(event, userId))
      .map((event) => ({ event, start: eventStart(event, timezone) }))
      .filter(({ start }) => start?.isValid() && start.isSame(selectedDay, "day"))
      .sort((a, b) => a.start.valueOf() - b.start.valueOf())
      .map((row, index, all) => ({
        ...row,
        // "Next up" only means something relative to the real current
        // moment — on a past/future selected date, every row would
        // otherwise (mis)qualify as "next" simply for being ahead of "now".
        isNext:
          isViewingToday &&
          row.start.isAfter(now) &&
          all.slice(0, index).every((other) => !other.start.isAfter(now))
      }))
  }, [events, timezone, userId, currentDate, isViewingToday])

  return (
    <div className={"rounded-xl border border-gray-200 p-3"}>
      <Flex align={"center"} justify={"space-between"} className={"mb-2"}>
        <Title level={5} className={"m-0"}>
          My Schedule
        </Title>
        <Button type={"link"} size={"small"} className={"px-0"} onClick={onViewAll}>
          View all
        </Button>
      </Flex>

      {!scheduleEvents.length && (
        <Text type={"secondary"} className={"text-xs"}>
          {isViewingToday
            ? "Nothing on your schedule today."
            : "Nothing on your schedule."}
        </Text>
      )}

      <Flex vertical gap={8}>
        {scheduleEvents.map(({ event, start, isNext }) => {
          const input = { title: event?.title, record: flattenEvent(event) }
          const { title, typeLabel } = calendarEventCardText(input, t)
          const { recordName } = calendarEventCardRecordParts(input, t)
          const color =
            getStoredEventColor(event?.id)?.border_color || event?.border_color

          return (
            <button
              key={event.id}
              type={"button"}
              onClick={() => onSelectEvent(event.id)}
              className={[
                "flex items-start gap-2 rounded-lg border p-2 text-left transition-colors hover:bg-gray-50",
                isNext ? "border-teal-300 bg-teal-50/60" : "border-gray-100"
              ].join(" ")}
            >
              <span
                className={"mt-1 size-2 shrink-0 rounded-full"}
                style={{ backgroundColor: color || "#94A3B8" }}
              />
              <div className={"min-w-0 flex-1"}>
                <Text
                  ellipsis={{ tooltip: title }}
                  className={"block text-xs font-semibold text-gray-800"}
                >
                  {title}
                </Text>
                {recordName && (
                  <Text
                    ellipsis={{ tooltip: recordName }}
                    className={"block text-xs text-gray-500"}
                  >
                    {recordName}
                  </Text>
                )}
                <CalendarTimeAndType
                  className={"text-[11px] text-gray-500"}
                  time={event?.all_day ? t("calendarEvent.allDay") : start.format("h:mm A")}
                  typeLabel={typeLabel}
                />
              </div>
            </button>
          )
        })}
      </Flex>
    </div>
  )
}
